import * as THREE from 'three';
import { StateMachine } from '../ai/StateMachine';
import { ProjectilePool } from '../weapons/ProjectilePool';
import { EventBus } from '../core/EventBus';

export interface TurretConfig {
  position: THREE.Vector3;
  detectionRange: number;
  attackRange: number;
  fireRate: number; // shots per second
  damage: number;
  rotationSpeed: number;
  health: number;
}

interface TurretContext {
  turret: Turret;
  target: THREE.Object3D | null;
  lastFireTime: number;
}

let turretIdCounter = 0;

export class Turret {
  public readonly id: string;
  public readonly mesh: THREE.Group;

  private config: TurretConfig;
  private stateMachine: StateMachine<TurretContext>;
  private context: TurretContext;
  private projectilePool: ProjectilePool;
  private playerRef: THREE.Object3D | null = null;

  private health: number;
  private isDestroyed: boolean = false;

  private headMesh: THREE.Group;
  private barrelMesh: THREE.Mesh;

  constructor(
    scene: THREE.Scene,
    projectilePool: ProjectilePool,
    config: TurretConfig
  ) {
    this.id = `turret_${turretIdCounter++}`;
    this.config = config;
    this.projectilePool = projectilePool;
    this.health = config.health;

    // Create mesh
    this.mesh = new THREE.Group();
    const { head, barrel } = this.createTurretMesh();
    this.headMesh = head;
    this.barrelMesh = barrel;

    this.mesh.position.copy(config.position);
    scene.add(this.mesh);

    // Set up hit detection data
    this.mesh.userData = {
      id: this.id,
      health: this.health,
      hitRadius: 2,
      points: 200,
      isTurret: true,
    };

    // Create AI context
    this.context = {
      turret: this,
      target: null,
      lastFireTime: 0,
    };

    // Set up state machine
    this.stateMachine = this.createStateMachine();
    this.stateMachine.setState('idle');

    // Listen for damage
    EventBus.on('target:damaged', (data) => {
      if (data.id === this.id) {
        this.health = data.healthRemaining;
        this.mesh.userData.health = this.health;
        this.onDamaged();
      }
    });

    EventBus.on('target:destroyed', (data) => {
      if (data.id === this.id) {
        this.destroy(scene);
      }
    });
  }

  private createTurretMesh(): { head: THREE.Group; barrel: THREE.Mesh } {
    // Base - hexagonal platform
    const baseGeometry = new THREE.CylinderGeometry(1.5, 1.8, 0.5, 6);
    const baseMaterial = new THREE.MeshStandardMaterial({
      color: 0x2a2a4e,
      metalness: 0.8,
      roughness: 0.3,
    });
    const base = new THREE.Mesh(baseGeometry, baseMaterial);
    base.position.y = 0.25;
    this.mesh.add(base);

    // Base glow ring
    const ringGeometry = new THREE.TorusGeometry(1.6, 0.05, 8, 6);
    const ringMaterial = new THREE.MeshStandardMaterial({
      color: 0xff0000,
      emissive: 0xff0000,
      emissiveIntensity: 2,
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 0.5;
    this.mesh.add(ring);

    // Rotating head
    const head = new THREE.Group();
    head.position.y = 0.7;
    this.mesh.add(head);

    // Head body
    const headGeometry = new THREE.BoxGeometry(1.2, 0.8, 1.2);
    const headMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a1a3e,
      metalness: 0.9,
      roughness: 0.2,
    });
    const headMesh = new THREE.Mesh(headGeometry, headMaterial);
    head.add(headMesh);

    // Sensor eye
    const eyeGeometry = new THREE.SphereGeometry(0.2, 16, 16);
    const eyeMaterial = new THREE.MeshStandardMaterial({
      color: 0xff0000,
      emissive: 0xff0000,
      emissiveIntensity: 3,
    });
    const eye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    eye.position.set(0, 0.1, 0.5);
    head.add(eye);

    // Barrel
    const barrelGeometry = new THREE.CylinderGeometry(0.1, 0.15, 1.5, 8);
    const barrelMaterial = new THREE.MeshStandardMaterial({
      color: 0x3a3a5e,
      metalness: 0.9,
      roughness: 0.1,
    });
    const barrel = new THREE.Mesh(barrelGeometry, barrelMaterial);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, 0, 1);
    head.add(barrel);

    // Barrel glow tip
    const tipGeometry = new THREE.CylinderGeometry(0.12, 0.12, 0.1, 8);
    const tipMaterial = new THREE.MeshStandardMaterial({
      color: 0xff4400,
      emissive: 0xff4400,
      emissiveIntensity: 2,
    });
    const tip = new THREE.Mesh(tipGeometry, tipMaterial);
    tip.rotation.x = Math.PI / 2;
    tip.position.set(0, 0, 1.75);
    head.add(tip);

    return { head, barrel };
  }

  private createStateMachine(): StateMachine<TurretContext> {
    const sm = new StateMachine<TurretContext>(this.context);

    // Idle state - scanning for targets
    sm.addState({
      name: 'idle',
      onEnter: () => {
        // Reset head rotation slowly
      },
      onUpdate: (ctx, dt) => {
        // Slow scan rotation
        this.headMesh.rotation.y += 0.5 * dt;

        // Look for player
        ctx.target = this.findTarget();
      },
    });

    // Alert state - target detected, rotating to face
    sm.addState({
      name: 'alert',
      onEnter: () => {
        // Could play alert sound
      },
      onUpdate: (ctx, dt) => {
        if (!ctx.target) return;
        this.rotateTowardsTarget(ctx.target, dt);
      },
    });

    // Attack state - firing at target
    sm.addState({
      name: 'attack',
      onUpdate: (ctx, dt) => {
        if (!ctx.target) return;
        this.rotateTowardsTarget(ctx.target, dt);
        this.tryFire(ctx);
      },
    });

    // Transitions
    sm.addTransition('idle', 'alert', (ctx) => {
      return ctx.target !== null && this.getDistanceToTarget(ctx.target) <= this.config.detectionRange;
    });

    sm.addTransition('alert', 'attack', (ctx) => {
      if (!ctx.target) return false;
      const distance = this.getDistanceToTarget(ctx.target);
      const isFacing = this.isFacingTarget(ctx.target);
      return distance <= this.config.attackRange && isFacing;
    });

    sm.addTransition('alert', 'idle', (ctx) => {
      return ctx.target === null || this.getDistanceToTarget(ctx.target) > this.config.detectionRange;
    });

    sm.addTransition('attack', 'alert', (ctx) => {
      if (!ctx.target) return true;
      return this.getDistanceToTarget(ctx.target) > this.config.attackRange;
    });

    sm.addTransition('attack', 'idle', (ctx) => {
      return ctx.target === null || this.getDistanceToTarget(ctx.target) > this.config.detectionRange;
    });

    return sm;
  }

  public setPlayer(player: THREE.Object3D): void {
    this.playerRef = player;
  }

  private findTarget(): THREE.Object3D | null {
    return this.playerRef;
  }

  private getDistanceToTarget(target: THREE.Object3D): number {
    return this.mesh.position.distanceTo(target.position);
  }

  private isFacingTarget(target: THREE.Object3D): boolean {
    const toTarget = new THREE.Vector3()
      .subVectors(target.position, this.mesh.position)
      .normalize();
    toTarget.y = 0;

    const forward = new THREE.Vector3(0, 0, 1);
    forward.applyQuaternion(this.headMesh.quaternion);
    forward.y = 0;
    forward.normalize();

    const dot = forward.dot(toTarget);
    return dot > 0.95; // Within ~18 degrees
  }

  private rotateTowardsTarget(target: THREE.Object3D, dt: number): void {
    const toTarget = new THREE.Vector3()
      .subVectors(target.position, this.mesh.position);
    toTarget.y = 0;

    const targetAngle = Math.atan2(toTarget.x, toTarget.z);
    let currentAngle = this.headMesh.rotation.y;

    // Normalize angles
    while (currentAngle > Math.PI) currentAngle -= Math.PI * 2;
    while (currentAngle < -Math.PI) currentAngle += Math.PI * 2;

    let angleDiff = targetAngle - currentAngle;
    while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
    while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

    const rotateAmount = Math.sign(angleDiff) * Math.min(
      Math.abs(angleDiff),
      this.config.rotationSpeed * dt
    );

    this.headMesh.rotation.y += rotateAmount;
  }

  private tryFire(ctx: TurretContext): void {
    const now = performance.now();
    const fireInterval = 1000 / this.config.fireRate;

    if (now - ctx.lastFireTime < fireInterval) return;

    ctx.lastFireTime = now;

    // Fire projectile
    const worldPos = new THREE.Vector3();
    this.barrelMesh.getWorldPosition(worldPos);
    worldPos.y += 0.7; // Adjust for barrel tip

    const forward = new THREE.Vector3(0, 0, 1);
    forward.applyQuaternion(this.headMesh.quaternion);

    // Fire position slightly in front of barrel
    const firePos = this.mesh.position.clone();
    firePos.y += 0.7;
    firePos.add(forward.clone().multiplyScalar(2));

    this.projectilePool.spawn(
      firePos,
      forward,
      80, // projectile speed
      this.config.damage,
      new THREE.Color(0xff4400),
      2
    );
  }

  private onDamaged(): void {
    // Flash effect
    this.headMesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const material = child.material as THREE.MeshStandardMaterial;
        const originalEmissive = material.emissive.clone();
        material.emissive.setHex(0xffffff);
        setTimeout(() => {
          material.emissive.copy(originalEmissive);
        }, 100);
      }
    });
  }

  private destroy(scene: THREE.Scene): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    // Explosion effect
    this.createExplosion(scene);

    scene.remove(this.mesh);
  }

  private createExplosion(scene: THREE.Scene): void {
    const position = this.mesh.position.clone();

    // Create explosion particles
    for (let i = 0; i < 15; i++) {
      const size = 0.2 + Math.random() * 0.4;
      const geometry = new THREE.BoxGeometry(size, size, size);
      const material = new THREE.MeshStandardMaterial({
        color: Math.random() > 0.5 ? 0xff4400 : 0xff0000,
        emissive: Math.random() > 0.5 ? 0xff4400 : 0xff0000,
        emissiveIntensity: 2,
      });
      const debris = new THREE.Mesh(geometry, material);

      debris.position.copy(position);
      debris.position.y += 0.5;

      debris.userData.velocity = new THREE.Vector3(
        (Math.random() - 0.5) * 15,
        5 + Math.random() * 10,
        (Math.random() - 0.5) * 15
      );

      scene.add(debris);

      // Animate
      const animate = () => {
        debris.userData.velocity.y -= 20 * 0.016;
        debris.position.add(debris.userData.velocity.clone().multiplyScalar(0.016));
        debris.rotation.x += 0.1;
        debris.rotation.y += 0.1;

        const mat = debris.material as THREE.MeshStandardMaterial;
        mat.opacity -= 0.02;

        if (debris.position.y < 0 || mat.opacity <= 0) {
          scene.remove(debris);
        } else {
          requestAnimationFrame(animate);
        }
      };
      const mat = debris.material as THREE.MeshStandardMaterial;
      mat.transparent = true;
      requestAnimationFrame(animate);
    }
  }

  public update(dt: number): void {
    if (this.isDestroyed) return;

    // Update AI
    this.context.target = this.findTarget();
    this.stateMachine.update(dt);
  }

  public getMesh(): THREE.Group {
    return this.mesh;
  }

  public isAlive(): boolean {
    return !this.isDestroyed;
  }

  public getState(): string | null {
    return this.stateMachine.getCurrentStateName();
  }
}
